// react
import { useEffect, useMemo, useState } from 'react';

// material ui
import { Box, Button, Stack } from '@mui/material';

// shared components
import InvertedSlider from '@/components/ui/slider/InvertedSlider';

// components
import { formatHour } from '@/utils/formatters';

const GRANULARITY = 4; // 15 minute steps
const DEFAULT_START = 7;
const DEFAULT_END = 17;

const DAYS = [
  { key: 'monday', label: 'Mandag' },
  { key: 'tuesday', label: 'Tirsdag' },
  { key: 'wednesday', label: 'Onsdag' },
  { key: 'thursday', label: 'Torsdag' },
  { key: 'friday', label: 'Fredag' },
  { key: 'saturday', label: 'Lørdag' },
  { key: 'sunday', label: 'Søndag' },
];

const emptyOpeningHours = () =>
  DAYS.reduce((acc, d) => {
    acc[d.key] = [];
    return acc;
  }, {});

const hoursToTicks = (hour) => Math.round(hour * GRANULARITY);
const ticksToHours = (ticks) => ticks / GRANULARITY;

const mapPayloadToLocal = (openingHoursPayload) => {
  const base = {};
  for (const { key } of DAYS) {
    const intervals = openingHoursPayload?.[key] ?? [];
    const first = intervals?.[0];
    if (
      first &&
      typeof first.start === 'number' &&
      typeof first.end === 'number'
    ) {
      base[key] = {
        active: true,
        startTicks: hoursToTicks(first.start),
        endTicks: hoursToTicks(first.end),
      };
    } else {
      base[key] = {
        active: false,
        startTicks: hoursToTicks(DEFAULT_START),
        endTicks: hoursToTicks(DEFAULT_END),
      };
    }
  }
  return base;
};

const mapLocalToPayload = (localState) => {
  const payload = emptyOpeningHours();
  for (const { key } of DAYS) {
    const day = localState[key];
    if (!day?.active) {
      payload[key] = [];
      continue;
    }

    const start = ticksToHours(day.startTicks);
    const end = ticksToHours(day.endTicks);

    // Basic guard: ensure start < end
    if (typeof start === 'number' && typeof end === 'number' && start < end) {
      payload[key] = [{ start, end }];
    } else {
      payload[key] = [];
    }
  }
  return payload;
};

const buildMarks = () => {
  // marks at each 2 hours + endpoints
  const marks = [];
  for (let h = 0; h <= 24; h += 2) {
    marks.push({ value: h * GRANULARITY, label: String(h) });
  }
  return marks;
};

export const OpeningHoursEditor = ({ value, onChange }) => {
  // value is payload shape; internal state is tick-based + active flag
  const initialLocal = useMemo(() => mapPayloadToLocal(value), [value]);
  const [local, setLocal] = useState(initialLocal);

  useEffect(() => {
    setLocal(initialLocal);
  }, [initialLocal]);

  const handleToggle = (dayKey) => {
    setLocal((prev) => {
      const next = {
        ...prev,
        [dayKey]: { ...prev[dayKey], active: !prev[dayKey]?.active },
      };
      onChange?.(mapLocalToPayload(next));
      return next;
    });
  };

  const handleRangeChange = (dayKey, newRange) => {
    if (!Array.isArray(newRange) || newRange.length !== 2) return;

    setLocal((prev) => {
      const next = {
        ...prev,
        [dayKey]: {
          ...prev[dayKey],
          active: true,
          startTicks: newRange[0],
          endTicks: newRange[1],
        },
      };
      onChange?.(mapLocalToPayload(next));
      return next;
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack spacing={1}>
        {DAYS.map(({ key, label }) => {
          const day = local[key];
          const isActive = Boolean(day?.active);

          const rangeValue =
            isActive &&
            typeof day?.startTicks === 'number' &&
            typeof day?.endTicks === 'number'
              ? [day.startTicks, day.endTicks]
              : [hoursToTicks(DEFAULT_START), hoursToTicks(DEFAULT_END)];

          return (
            <Box
              key={key}
              sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
            >
              <Box sx={{ minWidth: 90 }}>{label}</Box>

              <Button
                variant={isActive ? 'contained' : 'outlined'}
                color={isActive ? 'primary' : 'inherit'}
                size="small"
                onClick={() => handleToggle(key)}
                sx={{ maxWidth: 80, minWidth: 80 }}
              >
                {isActive ? 'Aktiv' : 'Inaktiv'}
              </Button>

              <Box sx={{ flex: 1 }}>
                {isActive && (
                  <InvertedSlider
                    isInverted={false}
                    min={0}
                    max={24 * GRANULARITY}
                    step={1}
                    value={rangeValue}
                    onChange={(_, newRange) => handleRangeChange(key, newRange)}
                    valueLabelDisplay="on"
                    valueLabelFormat={(v) => formatHour(v / GRANULARITY)}
                    marks={buildMarks()}
                    disableSwap
                    sx={{
                      my: 2,
                      '& .MuiSlider-thumb': {
                        '&::after': { content: '""', display: 'none' },
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
